import { createApiRequest } from "@/lib/api/createApiService";
import apiClient from "@/lib/api/client";
import { DATA_ENTRY_ENDPOINTS } from "./data-entry.endpoints";

const downloadBlob = ({ data, filename }) => {
  const url = URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

export const dataEntryService = {
  getFrameworks: async (params = {}, options = {}) => {
    return createApiRequest({
      url: DATA_ENTRY_ENDPOINTS.FRAMEWORKS,
      method: "GET",
      body: params,
      ...options,
    });
  },

  getDisclosures: async (params = {}, options = {}) => {
    return createApiRequest({
      url: DATA_ENTRY_ENDPOINTS.DISCLOSURES,
      method: "GET",
      body: params,
      ...options,
    });
  },

  getMasterDataDisclosures: async (params = {}, options = {}) => {
    return createApiRequest({
      url: DATA_ENTRY_ENDPOINTS.MASTER_DATA_DISCLOSURES,
      method: "GET",
      body: params,
      ...options,
    });
  },

  getDataEntriesByDisclosure: async (
    disclosureId,
    params = {},
    options = {},
  ) => {
    return createApiRequest({
      url: DATA_ENTRY_ENDPOINTS.DATA_ENTRIES_BY_DISCLOSURE(disclosureId),
      method: "GET",
      body: params,
      ...options,
    });
  },

  getDataEntriesByDisclosureComputed: async (
    disclosureId,
    params = {},
    options = {},
  ) => {
    return createApiRequest({
      url: DATA_ENTRY_ENDPOINTS.DATA_ENTRIES_BY_DISCLOSURE_COMPUTED(
        disclosureId,
      ),
      method: "GET",
      body: params,
      ...options,
    });
  },

  getEntriesByOrgYear: async (disclosureId, params = {}) => {
    return createApiRequest({
      url: DATA_ENTRY_ENDPOINTS.DATA_ENTRIES_BY_ORG_YEAR(disclosureId),
      method: "GET",
      body: params,
    });
  },

  bulkCreateDataEntries: async (payload = {}, options = {}) => {
    return createApiRequest({
      url: DATA_ENTRY_ENDPOINTS.DATA_ENTRIES_BULK,
      method: "POST",
      body: payload,
      ...options,
    });
  },

  bulkUpdateDataEntries: async (payload = {}, options = {}) => {
    return createApiRequest({
      url: DATA_ENTRY_ENDPOINTS.DATA_ENTRIES_BULK,
      method: "PUT",
      body: payload,
      ...options,
    });
  },

  updateDataEntry: async (entryId, payload = {}, options = {}) => {
    return createApiRequest({
      url: DATA_ENTRY_ENDPOINTS.DATA_ENTRIES(entryId),
      method: "PUT",
      body: payload,
      ...options,
    });
  },

  softDeleteDataEntry: async (entryId, payload = {}, options = {}) => {
    return createApiRequest({
      url: DATA_ENTRY_ENDPOINTS.DATA_ENTRIES(entryId),
      method: "PATCH",
      body: payload,
      ...options,
    });
  },

  bulkSoftDeleteDataEntries: async (entryIds = [], options = {}) => {
    return createApiRequest({
      url: DATA_ENTRY_ENDPOINTS.DATA_ENTRIES_BULK_DELETE,
      method: "PATCH",
      body: { entryIds },
      ...options,
    });
  },

  hardDeleteDataEntry: async (entryId, payload = {}, options = {}) => {
    return createApiRequest({
      url: DATA_ENTRY_ENDPOINTS.DATA_ENTRIES(entryId),
      method: "DELETE",
      body: payload,
      ...options,
    });
  },

  uploadProofFiles: async (companyId, files, options = {}) => {
    const formData = new FormData();
    for (const file of files) {
      formData.append("files", file);
    }
    return createApiRequest({
      url: DATA_ENTRY_ENDPOINTS.FILES_UPLOAD_BATCH(companyId),
      method: "POST",
      body: formData,
      isFormData: true,
      ...options,
    });
  },

  deleteProofFile: async (fileId, options = {}) => {
    return createApiRequest({
      url: DATA_ENTRY_ENDPOINTS.FILES_DELETE(fileId),
      method: "DELETE",
      ...options,
    });
  },

  listAssignments: async (params = {}, options = {}) => {
    return createApiRequest({
      url: DATA_ENTRY_ENDPOINTS.ASSIGNMENTS(),
      method: "GET",
      body: params,
      ...options,
    });
  },

  createAssignment: async (payload = {}, options = {}) => {
    return createApiRequest({
      url: DATA_ENTRY_ENDPOINTS.ASSIGNMENTS(),
      method: "POST",
      body: payload,
      ...options,
    });
  },

  removeAssignment: async (id, options = {}) => {
    return createApiRequest({
      url: DATA_ENTRY_ENDPOINTS.ASSIGNMENTS(id),
      method: "DELETE",
      ...options,
    });
  },

  getPrefillSuggestions: async (disclosureId, options = {}) => {
    return createApiRequest({
      url: DATA_ENTRY_ENDPOINTS.PREFILL_SUGGESTIONS(disclosureId),
      method: "GET",
      ...options,
    });
  },

  getNxmapImportPreview: async (params = {}, options = {}) => {
    return createApiRequest({
      url: DATA_ENTRY_ENDPOINTS.NXMAP_IMPORT_PREVIEW,
      method: "GET",
      body: params,
      ...options,
    });
  },

  syncNxmapEntries: async (payload = {}, options = {}) => {
    return createApiRequest({
      url: DATA_ENTRY_ENDPOINTS.NXMAP_SYNC,
      method: "POST",
      body: payload,
      ...options,
    });
  },

  syncNxmapBulk: async (payload = {}, options = {}) => {
    return createApiRequest({
      url: DATA_ENTRY_ENDPOINTS.NXMAP_SYNC_BULK_PREVIEW,
      method: "POST",
      body: payload,
      ...options,
    });
  },

  confirmNxmapBulkSync: async (payload = {}, options = {}) => {
    return createApiRequest({
      url: DATA_ENTRY_ENDPOINTS.NXMAP_SYNC_BULK_CONFIRM,
      method: "POST",
      body: payload,
      ...options,
    });
  },

  syncNxmapEntry: async (entryId, options = {}) => {
    return createApiRequest({
      url: DATA_ENTRY_ENDPOINTS.NXMAP_SYNC_SINGLE(entryId),
      method: "POST",
      ...options,
    });
  },

  listDataEntries: async (params = {}, options = {}) => {
    return createApiRequest({
      url: DATA_ENTRY_ENDPOINTS.DATA_ENTRIES_LIST,
      method: "GET",
      body: params,
      ...options,
    });
  },

  listComputedDisclosures: async (params = {}, options = {}) => {
    return createApiRequest({
      url: DATA_ENTRY_ENDPOINTS.COMPUTED_DISCLOSURES_LIST,
      method: "GET",
      body: params,
      ...options,
    });
  },

  listOverview: async (params = {}, options = {}) => {
    return createApiRequest({
      url: DATA_ENTRY_ENDPOINTS.DATA_ENTRIES_OVERVIEW,
      method: "GET",
      body: params,
      ...options,
    });
  },

  listConsolidated: async (params = {}, options = {}) => {
    return createApiRequest({
      url: DATA_ENTRY_ENDPOINTS.DATA_ENTRIES_CONSOLIDATED,
      method: "POST",
      body: params,
      ...options,
    });
  },

  listConsolidatedBreakdown: async (params = {}, options = {}) => {
    return createApiRequest({
      url: DATA_ENTRY_ENDPOINTS.DATA_ENTRIES_CONSOLIDATED_BREAKDOWN,
      method: "GET",
      body: params,
      ...options,
    });
  },

  getDisclosurePresence: async (options = {}) => {
    return createApiRequest({
      url: DATA_ENTRY_ENDPOINTS.DISCLOSURE_PRESENCE,
      method: "GET",
      ...options,
    });
  },

  downloadProofFile: async (proof) => {
    const fileId = proof?.fileId || proof?._id;
    if (!fileId) return;
    const { data } = await apiClient.get(`/files/download/${fileId}`, {
      responseType: "blob",
    });
    downloadBlob({
      data,
      filename: proof.name || proof.originalName || "download",
    });
  },

  parseBatchUpload: async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return createApiRequest({
      url: DATA_ENTRY_ENDPOINTS.BATCH_UPLOAD_PARSE,
      method: "POST",
      body: formData,
      isFormData: true,
    });
  },

  confirmBatchUpload: async (entries) => {
    return createApiRequest({
      url: DATA_ENTRY_ENDPOINTS.BATCH_UPLOAD_CONFIRM,
      method: "POST",
      body: entries,
    });
  },

  downloadBatchUploadTemplate: async ({
    framework,
    templateType = "without_data",
    organizationId,
    year,
    startYear,
    startMonth,
    endYear,
    endMonth,
    category,
    downloadFilename = "Data_Entry_Template.xlsx",
  }) => {
    const { default: apiClient } = await import("@/lib/api/client");
    const params = {
      framework,
      templateType,
      organizationId,
      startYear,
      startMonth,
      endYear,
      endMonth,
      category,
    };
    if (year !== undefined && year !== null) params.year = Number(year);

    const { data } = await apiClient.get(
      DATA_ENTRY_ENDPOINTS.BATCH_UPLOAD_TEMPLATE,
      { params, responseType: "blob" },
    );
    downloadBlob({
      data,
      filename: downloadFilename,
    });
  },

  getBatchUploadFilterOptions: async ({
    templateType = "without_data",
    organizationId,
    year,
    startYear,
    startMonth,
    endYear,
    endMonth,
    framework,
    category,
  }) => {
    return createApiRequest({
      url: DATA_ENTRY_ENDPOINTS.BATCH_UPLOAD_FILTER_OPTIONS,
      method: "GET",
      body: {
        templateType,
        organizationId,
        ...(year !== undefined && year !== null ? { year } : {}),
        startYear,
        startMonth,
        endYear,
        endMonth,
        framework,
        category,
      },
    });
  },

  getTargetsByDisclosure: async (disclosureId, year) => {
    return createApiRequest({
      url: `/target-setup/by-disclosure`,
      method: "GET",
      body: { disclosureId, year },
    });
  },
};
